// 单域名行 — 序号 + 域名 + API Key 下拉(L1/L2)+ DNS 状态徽章 + 刷新(zap)+ 删除。
import { useState } from 'react'
import { Zap, Trash2 } from 'lucide-react'
import { useToast } from '@dogdisk/shared'
import {
  useRefreshDomainDNS,
  useUpdateDomain,
  type DomainHealth,
  type JumpDomainRow,
} from '../../_lib/jumpdomain-api'
import { CredentialSelect } from './credential-select'

// HealthBadge — 探活结果(一键测试 / hourly cron 写入)。
// up=正常(绿) / down=异常(红) / unknown 或缺省=未测(灰)。
function HealthBadge({ row }: { row: JumpDomainRow }) {
  const map: Record<DomainHealth, { label: string; cls: string }> = {
    up: { label: '正常', cls: 'bg-green-100 text-green-700' },
    down: { label: '异常', cls: 'bg-red-100 text-red-700' },
    unknown: { label: '未测', cls: 'bg-gray-100 text-gray-500' },
  }
  const key: DomainHealth = row.health ?? 'unknown'
  const m = map[key]
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${m.cls}`}
      title={row.last_checked_at ? `上次测试:${new Date(row.last_checked_at).toLocaleString()}` : '尚未测试'}
      data-testid={`Ye71I-health-badge-${row.id}`}
      data-health={key}
    >
      {m.label}
    </span>
  )
}

function DnsBadge({ row }: { row: JumpDomainRow }) {
  const map = {
    synced: { label: '已生效', cls: 'bg-green-100 text-green-700' },
    pending: { label: 'DNS 待解析', cls: 'bg-amber-100 text-amber-700' },
    failed: { label: '失败', cls: 'bg-red-100 text-red-700' },
  } as const
  const key = row.dns_sync_status ?? 'pending'
  const m = map[key]
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${m.cls}`}
      title={row.dns_sync_error ?? undefined}
      data-testid={`Ye71I-dns-badge-${row.id}`}
      data-dns-status={key}
    >
      {m.label}
    </span>
  )
}

export function DomainRow({
  row,
  index,
  showKey,
  onDelete,
}: {
  row: JumpDomainRow
  index: number
  showKey: boolean
  onDelete: (row: JumpDomainRow) => void
}) {
  const refresh = useRefreshDomainDNS()
  const updateDomain = useUpdateDomain()
  const toast = useToast()
  const [refreshing, setRefreshing] = useState(false)

  const onRefresh = async () => {
    setRefreshing(true)
    try {
      await refresh.mutateAsync({ domain_id: row.id, target_ip: '0.0.0.0' })
    } catch {
      toast.push('刷新失败', { variant: 'error' })
    } finally {
      setRefreshing(false)
    }
  }

  const onRebind = async (credentialId: string) => {
    try {
      await updateDomain.mutateAsync({ id: row.id, credential_id: credentialId || null })
    } catch {
      toast.push('改绑失败', { variant: 'error' })
    }
  }

  return (
    <li
      className="flex flex-col gap-1 py-2"
      data-testid={`Ye71I-domain-${row.id}`}
    >
      <div className="flex items-center gap-3">
        <span className="w-5 text-xs text-gray-400">{index + 1}</span>
        <span className="flex-1 font-mono text-sm">{row.domain}</span>
        {showKey && (
          <CredentialSelect
            value={row.credential_id ?? ''}
            onChange={onRebind}
            disabled={updateDomain.isPending}
            testid={`Ye71I-cred-${row.id}`}
          />
        )}
        <HealthBadge row={row} />
        <DnsBadge row={row} />
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="text-gray-400 hover:text-blue-600 disabled:opacity-40"
          title="刷新 DNS"
          data-testid={`Ye71I-refresh-${row.id}`}
        >
          <Zap className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(row)}
          className="text-gray-400 hover:text-red-600"
          title="删除域名"
          data-testid={`Ye71I-delete-domain-${row.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {/* 系统写、agent 只读:自动禁用原因(真人测速 cron 写入);仅展示 */}
      {row.notes && (
        <p
          className="ml-8 text-[11px] text-red-500"
          data-testid={`Ye71I-note-${row.id}`}
        >
          {row.notes}
        </p>
      )}
    </li>
  )
}
